import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'

// Debug the API base URL
console.log('API_BASE_URL:', API_BASE_URL)
console.log('Environment variables:', import.meta.env)

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    serialize: (params) => {
      // Custom parameter serialization to handle empty values and malformed parameters
      const searchParams = new URLSearchParams()
      Object.keys(params).forEach(key => {
        let value = params[key]
        // Clean up value - remove any trailing :number that might be added by browser
        if (typeof value === 'string') {
          value = value.replace(/:(\d+)$/, '')
        }
        if (value !== '' && value !== null && value !== undefined) {
          searchParams.append(key, value)
        }
      })
      return searchParams.toString()
    }
  }
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Try Supabase token first, fallback to old token
    const supabaseToken = localStorage.getItem('supabase_token')
    const oldToken = localStorage.getItem('token')
    const token = supabaseToken || oldToken
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // Debug logging
    console.log('Request config:', {
      url: config.url,
      baseURL: config.baseURL,
      params: config.params,
      fullURL: config.baseURL + config.url,
      tokenType: supabaseToken ? 'supabase' : 'legacy'
    })
    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        params: error.config?.params
      }
    })
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('supabase_token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  verifyToken: () => api.get('/auth/verify'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (updates) => api.put('/auth/profile', updates),
}

// Challenges API
export const challengesAPI = {
  getAll: (filters = {}) => {
    // Clean up empty string values to avoid malformed URLs
    const cleanFilters = {}
    Object.keys(filters).forEach(key => {
      if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
        cleanFilters[key] = filters[key]
      }
    })
    console.log('Making API request with filters:', cleanFilters)
    console.log('Base URL:', API_BASE_URL)
    return api.get('/challenges', { params: cleanFilters })
  },
  getById: (id) => api.get(`/challenges/${id}`),
  create: (challengeData) => api.post('/challenges', challengeData),
  update: (id, updates) => api.put(`/challenges/${id}`, updates),
  delete: (id) => api.delete(`/challenges/${id}`),
  getCategories: () => api.get('/challenges/meta/categories'),
  join: (id) => api.post(`/challenges/${id}/join`),
  leave: (id) => api.post(`/challenges/${id}/leave`),
  checkParticipation: (id) => api.get(`/challenges/${id}/participation`),
  getParticipants: (id, params = {}) => api.get(`/challenges/${id}/participants`, { params }),
}

// Submissions API
export const submissionsAPI = {
  create: (submissionData) => {
    const formData = new FormData()
    Object.keys(submissionData).forEach(key => {
      if (key === 'files' && submissionData[key]) {
        Array.from(submissionData[key]).forEach(file => {
          formData.append('files', file)
        })
      } else if (submissionData[key] !== undefined) {
        formData.append(key, submissionData[key])
      }
    })
    return api.post('/submissions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  getAll: (params = {}) => api.get('/submissions', { params }), // Admin only
  getMySubmissions: (params = {}) => api.get('/submissions/my-submissions', { params }),
  getByChallenge: (challengeId, params = {}) => api.get(`/submissions/challenge/${challengeId}`, { params }),
  getByUserAndChallenge: (userId, challengeId) => api.get(`/submissions/user/${userId}/challenge/${challengeId}`),
  getById: (id) => api.get(`/submissions/${id}`),
  update: (id, updates) => {
    const formData = new FormData()
    Object.keys(updates).forEach(key => {
      if (key === 'files' && updates[key]) {
        Array.from(updates[key]).forEach(file => {
          formData.append('files', file)
        })
      } else if (updates[key] !== undefined) {
        formData.append(key, updates[key])
      }
    })
    return api.put(`/submissions/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  delete: (id) => api.delete(`/submissions/${id}`),
  evaluate: (id, evaluation) => api.put(`/submissions/${id}/evaluate`, evaluation),
}

// Leaderboard API
export const leaderboardAPI = {
  getByChallenge: (challengeId, params = {}) => api.get(`/leaderboard/challenge/${challengeId}`, { params }),
  getGlobal: (params = {}) => api.get('/leaderboard/global', { params }),
  getByCategory: (category, params = {}) => api.get(`/leaderboard/category/${category}`, { params }),
  getUserRankings: (userId) => api.get(`/leaderboard/user/${userId}`),
}

// Users API
export const usersAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  getStats: (id) => api.get(`/users/${id}/stats`),
  getAchievements: (id) => api.get(`/users/${id}/achievements`),
}

export default api